import {FC, useCallback, useEffect, useState, cloneElement} from 'react';
import {InteractionLoadable} from '@liveComponents/Loadable';
import {PremiumPaymentModal, PremiumPaymentModalProps} from '@liveComponents/Payment/PremiumPaymentModal';
import {AccountType} from '.cache/__types__';
import {LoginRequiredAction, LoginRequiredActionProps} from '@liveComponents/Auth';
import {useUser} from "@utils/user";
import {useSession} from "next-auth/react";
import {Spinner} from "@components/Spinner";

export type PremiumRequiredActionProps = {
    children: React.ReactElement;
    loginRequiredInitialPage: LoginRequiredActionProps['initialPage'];
    loginRequiredInitialPageProps?: Record<string, any>;
    lead: PremiumPaymentModalProps['lead'];
    text: PremiumPaymentModalProps['text'];
};

export const PremiumRequiredAction: FC<PremiumRequiredActionProps> = (
    {
        children,
        lead,
        text,
        loginRequiredInitialPage,
        loginRequiredInitialPageProps
    }
) => {
    const [isOpen, setOpen] = useState<boolean>(false);
    const {status} = useSession();
    const {user, refetch} = useUser();

    const closeModal = useCallback(() => {
        setOpen(false);
    }, []);

    const processClick = useCallback(() => {
        if (user?.accountType === AccountType.Premium) {
            children.props.onClick && children.props.onClick();
        } else {
            setOpen(true);
        }
    }, [user, children]);

    const processSuccess = useCallback(() => {
        // TODO if able
        closeModal();
        children.props.onClick && children.props.onClick();
    }, [closeModal, children]);

    useEffect(() => {
        if (isOpen && user?.accountType === AccountType.Premium) {
            setOpen(false);
            children.props.onClick && children.props.onClick();
        } else if (isOpen) {
            refetch(); // Try to get actual information for user
        }
    }, [isOpen, user, children, refetch]);

    return (
        <>
            <LoginRequiredAction initialPage={loginRequiredInitialPage}
                                 initialPageProps={loginRequiredInitialPageProps}>
                {cloneElement(children, Object.assign({}, children.props, {onClick: processClick}))}
            </LoginRequiredAction>

            {isOpen && status === "loading" && (
                <Spinner animation={"border"} overlay fullPage />
            )}

            <InteractionLoadable show={isOpen && status !== "loading"} keepRendered>
                <PremiumPaymentModal show={isOpen && status !== "loading"} onHide={closeModal} lead={lead} text={text}/>
            </InteractionLoadable>
        </>
    );
};
